// ============================================================================
// Deep Link Service — обработка deep-link параметров из Max Messenger
// ----------------------------------------------------------------------------
// Читает start_param из window.WebApp.initDataUnsafe.start_param (Max Messenger)
// и ?qrToken= из URL query string. Нормализует в токен для авторизации.
// ============================================================================

// Константы для sessionStorage
const SESSION_KEY = "pending_qr_token";

// Паттерны токенов (синхронизированы с ScanPage.tsx)
const AUTH_TOKEN_PATTERN = /^[A-Z0-9]{6}$/;

/**
 * Пытается извлечь qrToken из start_param (Max Messenger deep-link).
 * start_param может быть как строкой, так и объектом-мапой.
 */
function extractTokenFromStartParam(startParam: unknown): string | null {
  if (!startParam) return null;

  // Если start_param — строка, пытаемся распарсить как URL-query или JSON
  if (typeof startParam === "string") {
    const trimmed = startParam.trim();

    // Прямое совпадение с паттерном токена
    if (AUTH_TOKEN_PATTERN.test(trimmed)) return trimmed;

    // Попытка распарсить как query string: qrToken=ABC123 или qr=ABC123
    try {
      const params = new URLSearchParams(trimmed);
      const fromQrToken = params.get("qrToken") ?? params.get("qr") ?? "";
      if (fromQrToken && AUTH_TOKEN_PATTERN.test(fromQrToken))
        return fromQrToken;
    } catch {
      // не URL-query — игнорируем
    }

    // Попытка распарсить как JSON: { "qrToken": "ABC123" }
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const fromJson = String(parsed.qrToken ?? parsed.qr ?? parsed.auth ?? "");
      if (fromJson && AUTH_TOKEN_PATTERN.test(fromJson)) return fromJson;
    } catch {
      // не JSON — игнорируем
    }

    return null;
  }

  // Если start_param — объект (WebAppStartParam map)
  if (typeof startParam === "object" && startParam !== null) {
    const map = startParam as Record<string, unknown>;
    const fromMap = String(
      map.qrToken ?? map.qr ?? map.auth ?? map.token ?? "",
    );
    if (fromMap && AUTH_TOKEN_PATTERN.test(fromMap)) return fromMap;
  }

  return null;
}

/**
 * Пытается извлечь qrToken из URL query string (?qrToken=ABC123).
 */
function extractTokenFromUrl(): string | null {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const fromUrl =
      searchParams.get("qrToken") ?? searchParams.get("token") ?? "";
    if (fromUrl && AUTH_TOKEN_PATTERN.test(fromUrl)) return fromUrl;
  } catch {
    // игнорируем ошибки парсинга URL
  }
  return null;
}

/**
 * Пытается извлечь qrToken из window.WebApp.initDataUnsafe (Max Messenger).
 * Логирует результат для отладки.
 */
export function detectDeepLinkToken(): string | null {
  // 1. Пробуем URL query string (прямой переход по ссылке)
  const fromUrl = extractTokenFromUrl();
  if (fromUrl) {
    console.log("[DeepLink] Token found in URL query:", fromUrl);
    return fromUrl;
  }

  // 2. Пробуем start_param из Max Messenger
  if (
    typeof window !== "undefined" &&
    window.WebApp?.initDataUnsafe?.start_param
  ) {
    const startParam = window.WebApp.initDataUnsafe.start_param;
    console.log("[DeepLink] start_param detected:", JSON.stringify(startParam));
    const fromStartParam = extractTokenFromStartParam(startParam);
    if (fromStartParam) {
      console.log(
        "[DeepLink] Token extracted from start_param:",
        fromStartParam,
      );
      return fromStartParam;
    }
    console.log("[DeepLink] start_param present but no valid token found");
  }

  return null;
}

/**
 * Сохраняет qrToken в sessionStorage для использования после авторизации.
 */
export function storePendingToken(token: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, token);
    console.log("[DeepLink] Pending token saved to sessionStorage:", token);
  } catch (err) {
    console.warn("[DeepLink] Failed to save pending token:", err);
  }
}

/**
 * Извлекает и удаляет сохранённый qrToken из sessionStorage.
 */
export function consumePendingToken(): string | null {
  try {
    const token = sessionStorage.getItem(SESSION_KEY);
    if (token) {
      sessionStorage.removeItem(SESSION_KEY);
      console.log("[DeepLink] Pending token consumed:", token);
      return token;
    }
  } catch (err) {
    console.warn("[DeepLink] Failed to read pending token:", err);
  }
  return null;
}

/**
 * Проверяет, есть ли сохранённый ожидающий токен (без удаления).
 */
export function hasPendingToken(): boolean {
  try {
    return !!sessionStorage.getItem(SESSION_KEY);
  } catch {
    return false;
  }
}
